import {
  useActiveNotificationsQuery,
  useNotificationSeenMutation,
} from "@api/notification";
import {
  BellIcon,
  Cog8ToothIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useIsUserRole } from "@hooks/auth";
import { memo, useId } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import remarkGfm from "remark-gfm";

export const NotificationButton = memo(({ drawerId }: { drawerId: string }) => {
  const auth = useAuth();
  const { data: notifications } = useActiveNotificationsQuery(undefined, {
    skip: !auth.isAuthenticated,
  });
  const { t } = useTranslation("notification");

  if (!auth.user) {
    return null;
  }

  return (
    <div
      className="tooltip tooltip-bottom indicator"
      data-tip={t("notifications")}
    >
      <label
        htmlFor={drawerId}
        className="drawer-button btn btn-ghost btn-sm btn-circle"
      >
        <BellIcon className="h-5 w-5" />
        {notifications && notifications.length > 0 && (
          <span className="badge badge-sm badge-error indicator-item">
            {notifications.length}
          </span>
        )}
      </label>
    </div>
  );
});

NotificationButton.displayName = "NotificationButton";

export const NotificationsDrawerContent = memo(
  ({ drawerId }: { drawerId: string }) => {
    const navigate = useNavigate();
    const isAdmin = useIsUserRole("admin");
    const auth = useAuth();
    const { data: notifications } = useActiveNotificationsQuery(undefined, {
      skip: !auth.isAuthenticated,
    });
    const [notificationSeen] = useNotificationSeenMutation();
    const { t } = useTranslation("notification");

    const handleMarkAsSeen = async (id: string) => {
      await notificationSeen(id);
    };

    return (
      <div className="drawer-side">
        <label
          htmlFor={drawerId}
          aria-label="close sidebar"
          className="drawer-overlay"
        />
        <div className="flex h-full w-80 flex-col bg-base-100 p-4 text-base-content">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-lg">{t("notifications")}</h2>
            <label
              htmlFor={drawerId}
              className="btn btn-ghost btn-sm btn-circle"
              aria-label="close notifications"
            >
              <XMarkIcon className="h-5 w-5" />
            </label>
          </div>

          <ul className="flex flex-grow flex-col gap-2 overflow-y-auto ">
            {isAdmin && (
              <>
                <button
                  type="button"
                  className="btn flex w-full gap-2 bg-base-200 p-3"
                  onClick={() => navigate("/admin/notifications")}
                >
                  <Cog8ToothIcon className="h-4 w-4" />
                  <span>{t("notificationManagement")}</span>
                </button>
                <div className="divider" />
              </>
            )}
            {notifications && notifications.length === 0 ? (
              <div className="rounded bg-base-200 p-8 text-center">
                {t("noNewNotifications")}
              </div>
            ) : (
              notifications?.map((notification) => (
                <li key={notification.id} className="relative">
                  <div className="flex w-full flex-col items-start gap-1 rounded-md bg-base-200 p-3">
                    <span className="font-medium">{notification.title}</span>
                    <div className="prose-markdown w-full max-w-full">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {notification.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleMarkAsSeen(notification.id);
                    }}
                    className="btn btn-ghost btn-xs btn-circle absolute top-2 right-2"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    );
  },
);

NotificationsDrawerContent.displayName = "NotificationsDrawerContent";

const NotificationsDrawer = memo(() => {
  const drawerId = useId();

  return (
    <>
      <div className="drawer drawer-end">
        <input id={drawerId} type="checkbox" className="drawer-toggle" />
        <NotificationsDrawerContent drawerId={drawerId} />
      </div>
      <NotificationButton drawerId={drawerId} />
    </>
  );
});

NotificationsDrawer.displayName = "NotificationsDrawer";

export default NotificationsDrawer;
